import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemedCard } from '../components/ThemedCard';
import { ThemedButton } from '../components/ThemedButton';
import { useTheme } from '../lib/theme-provider';
import GameHeader from '../components/GameHeader';
import MissionProgress from '../components/MissionProgress';
import GamePlayerList from '../components/GamePlayerList';
import AssassinationModal from '../components/AssassinationModal';
import MVPVotingModal from '../components/MVPVotingModal';
import LadyOfLakeModal from '../components/LadyOfLakeModal';
import ExcaliburModal from '../components/ExcaliburModal';
import GameNotesModal from '../components/GameNotesModal';
import IdentityRevealModal from '../components/IdentityRevealModal';
import GameHistoryModal from '../components/GameHistoryModal';
import GameRulesModal from '../components/GameRulesModal';
import { Crown, Check, X, Users, LogOut, RefreshCw } from 'lucide-react';

// 游戏阶段类型
type GamePhase = 'nomination' | 'voting' | 'executing' | 'settled';

// 玩家类型
interface Player {
  id: string;
  seatNumber: number;
  nickname: string;
  isLeader: boolean;
  isLadyOfLake: boolean;
  hasVoted: boolean;
  missionResult?: 'success' | 'fail';
  isCurrentPlayer: boolean;
  isOnline: boolean;
}

// 任务配置
const MISSION_CONFIG: Record<number, { players: number; needFails: number }> = {
  1: { players: 3, needFails: 1 },
  2: { players: 4, needFails: 1 },
  3: { players: 4, needFails: 1 },
  4: { players: 5, needFails: 2 },
  5: { players: 5, needFails: 1 },
};

export default function GamePlay() {
  const { theme } = useTheme();
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();

  // 游戏状态
  const [currentRound, setCurrentRound] = useState(1);
  const [phase, setPhase] = useState<GamePhase>('nomination');
  const [players, setPlayers] = useState<Player[]>([]);
  const [nominatedSeats, setNominatedSeats] = useState<number[]>([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [hasExecuted, setHasExecuted] = useState(false);
  const [missionResult, setMissionResult] = useState<'success' | 'fail' | null>(null);
  const [voteResult, setVoteResult] = useState<{ approve: number; reject: number } | null>(null);
  const [isSpectator, setIsSpectator] = useState(false);

  // 弹窗状态
  const [showAssassination, setShowAssassination] = useState(false);
  const [showMVPVoting, setShowMVPVoting] = useState(false);
  const [showLadyOfLake, setShowLadyOfLake] = useState(false);
  const [showExcalibur, setShowExcalibur] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showIdentity, setShowIdentity] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [gameNotes, setGameNotes] = useState('');

  // 模拟数据
  useEffect(() => {
    setPlayers([
      { id: '1', seatNumber: 1, nickname: 'Drunk', isLeader: true, isLadyOfLake: false, hasVoted: false, isCurrentPlayer: true, isOnline: true },
      { id: '2', seatNumber: 2, nickname: '帅哥', isLeader: false, isLadyOfLake: false, hasVoted: false, isCurrentPlayer: false, isOnline: true },
      { id: '3', seatNumber: 3, nickname: '才才', isLeader: false, isLadyOfLake: true, hasVoted: false, isCurrentPlayer: false, isOnline: true },
      { id: '4', seatNumber: 4, nickname: '昕宇', isLeader: false, isLadyOfLake: false, hasVoted: false, isCurrentPlayer: false, isOnline: true },
      { id: '5', seatNumber: 5, nickname: '周鼠鼠', isLeader: false, isLadyOfLake: false, hasVoted: false, isCurrentPlayer: false, isOnline: true },
      { id: '6', seatNumber: 6, nickname: 'boyixi', isLeader: false, isLadyOfLake: false, hasVoted: false, isCurrentPlayer: false, isOnline: true },
      { id: '7', seatNumber: 7, nickname: 'Hy', isLeader: false, isLadyOfLake: false, hasVoted: false, isCurrentPlayer: false, isOnline: true },
      { id: '8', seatNumber: 8, nickname: '丁丁', isLeader: false, isLadyOfLake: false, hasVoted: false, isCurrentPlayer: false, isOnline: true },
      { id: '9', seatNumber: 9, nickname: '好人火火', isLeader: false, isLadyOfLake: false, hasVoted: false, isCurrentPlayer: false, isOnline: true },
      { id: '10', seatNumber: 10, nickname: '还是要用...', isLeader: false, isLadyOfLake: false, hasVoted: false, isCurrentPlayer: false, isOnline: true },
    ]);
  }, []);

  // 生成任务进度
  const missions = Array.from({ length: 5 }, (_, i) => ({
    round: i + 1,
    players: MISSION_CONFIG[i + 1]?.players || 3,
    status:
      i + 1 < currentRound
        ? Math.random() > 0.5 ? 'success' : 'fail'
        : i + 1 === currentRound
        ? 'current'
        : 'pending',
  }));

  // 当前队长
  const currentLeader = players.find((p) => p.isLeader);
  const currentPlayer = players.find((p) => p.isCurrentPlayer);
  const isLeader = currentPlayer?.isLeader || false;

  // 处理提名
  const handleNominate = (seatNumber: number) => {
    if (!isLeader || phase !== 'nomination') return;
    const missionPlayers = MISSION_CONFIG[currentRound]?.players || 3;
    
    if (nominatedSeats.includes(seatNumber)) {
      setNominatedSeats(nominatedSeats.filter((s) => s !== seatNumber));
    } else if (nominatedSeats.length < missionPlayers) {
      setNominatedSeats([...nominatedSeats, seatNumber]);
    }
  };

  // 确认组队
  const handleConfirmNomination = () => {
    const missionPlayers = MISSION_CONFIG[currentRound]?.players || 3;
    if (nominatedSeats.length !== missionPlayers) {
      alert(`需要选择 ${missionPlayers} 名玩家`);
      return;
    }
    setPhase('voting');
  };

  // 投票
  const handleVote = (approve: boolean) => {
    if (hasVoted || isSpectator) return;
    setHasVoted(true);
    // TODO: 同步到Supabase
    
    // 模拟投票结果
    setTimeout(() => {
      setVoteResult({ approve: 6, reject: 4 });
      if (approve) {
        setPhase('executing');
      } else {
        // 投票失败，进入下一轮
        setPhase('nomination');
        setNominatedSeats([]);
        setHasVoted(false);
      }
    }, 1000);
  };

  // 执行任务
  const handleExecute = (success: boolean) => {
    if (hasExecuted || isSpectator) return;
    setHasExecuted(true);
    // TODO: 同步到Supabase
    
    setTimeout(() => {
      setMissionResult(success ? 'success' : 'fail');
      setPhase('settled');
    }, 1000);
  };

  // 进入下一轮
  const handleNextRound = () => {
    setCurrentRound((prev) => prev + 1);
    setPhase('nomination');
    setNominatedSeats([]);
    setHasVoted(false);
    setHasExecuted(false);
    setMissionResult(null);
    setVoteResult(null);
    // 轮换队长
    setPlayers((prev) => {
      const currentLeaderIndex = prev.findIndex((p) => p.isLeader);
      const nextLeaderIndex = (currentLeaderIndex + 1) % prev.length;
      return prev.map((p, i) => ({
        ...p,
        isLeader: i === nextLeaderIndex,
      }));
    });
  };

  // 渲染中间内容区域
  const renderCenterContent = () => {
    switch (phase) {
      case 'nomination':
        return (
          <div className="flex flex-col items-center justify-center py-8">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: theme.colors['text-primary'] }}
            >
              提名阶段
            </h2>
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
              style={{
                backgroundColor: `${theme.colors['accent-gold']}20`,
                border: `2px solid ${theme.colors['accent-gold']}`,
              }}
            >
              <Crown size={40} style={{ color: theme.colors['accent-gold'] }} />
            </div>
            <p style={{ color: theme.colors['text-secondary'] }}>
              {isLeader
                ? `请选择 ${MISSION_CONFIG[currentRound]?.players || 3} 名玩家`
                : `等待队长 ${currentLeader?.nickname} 提名`}
            </p>

            {/* 已选择的玩家 */}
            {nominatedSeats.length > 0 && (
              <div className="flex gap-2 mt-4">
                {nominatedSeats.map((seat) => (
                  <div
                    key={seat}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{
                      backgroundColor: '#4ade80',
                      color: theme.colors['bg-primary'],
                    }}
                  >
                    {seat}
                  </div>
                ))}
              </div>
            )}

            {/* 确认按钮 */}
            {isLeader && nominatedSeats.length === (MISSION_CONFIG[currentRound]?.players || 3) && (
              <ThemedButton className="mt-6" onClick={handleConfirmNomination}>
                确认组队
              </ThemedButton>
            )}
          </div>
        );

      case 'voting':
        return (
          <div className="flex flex-col items-center justify-center py-8">
            <h2
              className="text-2xl font-bold mb-2"
              style={{ color: theme.colors['accent-gold'] }}
            >
              投票阶段
            </h2>
            <p
              className="mb-6"
              style={{ color: theme.colors['text-secondary'] }}
            >
              当前队长: {currentLeader?.nickname}
            </p>

            {/* 提名的玩家 */}
            <div className="flex gap-2 mb-6">
              {nominatedSeats.map((seat) => (
                <div
                  key={seat}
                  className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                  style={{
                    backgroundColor: '#4ade80',
                    color: theme.colors['bg-primary'],
                    border: `2px solid #4ade80`,
                  }}
                >
                  {seat}
                </div>
              ))}
            </div>

            {/* 投票进度 */}
            <p
              className="mb-6 text-sm"
              style={{ color: theme.colors['text-secondary'] }}
            >
              已投票: {players.filter((p) => p.hasVoted).length}/{players.length} 人
            </p>

            {/* 投票按钮 */}
            {!hasVoted && !isSpectator && (
              <div className="flex gap-4">
                <ThemedButton
                  variant="secondary"
                  onClick={() => handleVote(false)}
                >
                  <X size={20} className="mr-2" />
                  反对
                </ThemedButton>
                <ThemedButton onClick={() => handleVote(true)}>
                  <Check size={20} className="mr-2" />
                  同意
                </ThemedButton>
              </div>
            )}

            {hasVoted && (
              <p style={{ color: theme.colors['text-secondary'] }}>
                等待其他玩家投票...
              </p>
            )}

            {isSpectator && (
              <p style={{ color: theme.colors['text-muted'] }}>
                观战中，无法投票
              </p>
            )}
          </div>
        );

      case 'executing':
        const isNominated = nominatedSeats.includes(currentPlayer?.seatNumber || 0);
        return (
          <div className="flex flex-col items-center justify-center py-8">
            <h2
              className="text-2xl font-bold mb-4"
              style={{ color: '#4ade80' }}
            >
              任务执行
            </h2>
            <p
              className="mb-4"
              style={{ color: theme.colors['text-secondary'] }}
            >
              第{currentRound}轮任务
            </p>

            {/* 任务队员 */}
            <div className="flex gap-2 mb-6">
              {nominatedSeats.map((seat) => (
                <div
                  key={seat}
                  className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
                  style={{
                    backgroundColor: '#4ade80',
                    color: theme.colors['bg-primary'],
                    border: `2px solid #4ade80`,
                  }}
                >
                  {seat}
                </div>
              ))}
            </div>

            {/* 执行按钮 */}
            {isNominated && !hasExecuted && !isSpectator && (
              <div className="flex gap-4">
                <ThemedButton
                  variant="secondary"
                  onClick={() => handleExecute(false)}
                >
                  <X size={20} className="mr-2" />
                  执行失败
                </ThemedButton>
                <ThemedButton onClick={() => handleExecute(true)}>
                  <Check size={20} className="mr-2" />
                  执行成功
                </ThemedButton>
              </div>
            )}

            {isNominated && hasExecuted && (
              <p style={{ color: theme.colors['text-secondary'] }}>
                已提交，等待其他队员...
              </p>
            )}

            {!isNominated && (
              <p style={{ color: theme.colors['text-secondary'] }}>
                等待任务队员执行...
              </p>
            )}

            {isSpectator && (
              <p style={{ color: theme.colors['text-muted'] }}>
                观战中，无法执行任务
              </p>
            )}
          </div>
        );

      case 'settled':
        return (
          <div className="flex flex-col items-center justify-center py-8">
            {/* 任务结果 */}
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center mb-4"
              style={{
                backgroundColor:
                  missionResult === 'success' ? '#4ade8020' : '#f8717120',
                border: `4px solid ${
                  missionResult === 'success' ? '#4ade80' : '#f87171'
                }`,
              }}
            >
              <Check
                size={48}
                style={{
                  color: missionResult === 'success' ? '#4ade80' : '#f87171',
                }}
              />
            </div>

            <h2
              className="text-2xl font-bold mb-2"
              style={{
                color: missionResult === 'success' ? '#4ade80' : '#f87171',
              }}
            >
              任务{missionResult === 'success' ? '成功' : '失败'}
            </h2>
            <p
              className="mb-6"
              style={{ color: theme.colors['text-secondary'] }}
            >
              第{currentRound}轮任务
            </p>

            {/* 投票结果 */}
            {voteResult && (
              <div className="flex gap-8 mb-6">
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mb-2"
                    style={{
                      backgroundColor: '#4ade80',
                      color: theme.colors['bg-primary'],
                    }}
                  >
                    {voteResult.approve}
                  </div>
                  <span style={{ color: '#4ade80' }}>成功</span>
                </div>
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mb-2"
                    style={{
                      backgroundColor: '#f87171',
                      color: theme.colors['bg-primary'],
                    }}
                  >
                    {voteResult.reject}
                  </div>
                  <span style={{ color: '#f87171' }}>失败</span>
                </div>
              </div>
            )}

            {/* 下一轮按钮 */}
            <ThemedButton onClick={handleNextRound}>进入下一轮</ThemedButton>
          </div>
        );
    }
  };

  return (
    <div
      className="min-h-screen pb-20"
      style={{ backgroundColor: theme.colors['bg-primary'] }}
    >
      {/* 头部 */}
      <GameHeader
        roomCode="OLTP05"
        currentRound={currentRound}
        extensions={{ ladyOfLake: true, excalibur: false }}
        forcedRound="round3"
        publicVote={true}
        onBack={() => navigate('/online-lobby')}
        onRefresh={() => window.location.reload()}
        onNotes={() => setShowNotes(true)}
        onIdentity={() => setShowIdentity(true)}
        onHistory={() => setShowHistory(true)}
        onRules={() => setShowRules(true)}
      />

      {/* 任务进度 */}
      <div className="px-4 py-2">
        <MissionProgress missions={missions} />
      </div>

      {/* 主内容区 */}
      <main className="px-4 py-4">
        {/* 玩家列表 + 中间内容 */}
        <div className="flex gap-4">
          {/* 左侧玩家 */}
          <div className="w-16 flex flex-col gap-2">
            {players
              .filter((p) => p.seatNumber <= 5)
              .map((player) => (
                <PlayerAvatar
                  key={player.id}
                  player={player}
                  isLeader={player.isLeader}
                  isNominated={nominatedSeats.includes(player.seatNumber)}
                  onClick={() => handleNominate(player.seatNumber)}
                  canClick={isLeader && phase === 'nomination'}
                />
              ))}
          </div>

          {/* 中间内容 */}
          <ThemedCard className="flex-1 min-h-[300px]">
            {renderCenterContent()}
          </ThemedCard>

          {/* 右侧玩家 */}
          <div className="w-16 flex flex-col gap-2">
            {players
              .filter((p) => p.seatNumber > 5)
              .map((player) => (
                <PlayerAvatar
                  key={player.id}
                  player={player}
                  isLeader={player.isLeader}
                  isNominated={nominatedSeats.includes(player.seatNumber)}
                  onClick={() => handleNominate(player.seatNumber)}
                  canClick={isLeader && phase === 'nomination'}
                />
              ))}
          </div>
        </div>
      </main>

      {/* 底部操作栏 */}
      <footer
        className="fixed bottom-0 left-0 right-0 flex items-center justify-between px-4 py-3 border-t"
        style={{
          backgroundColor: theme.colors['bg-card'],
          borderColor: theme.colors['border-primary'],
        }}
      >
        <ThemedButton variant="secondary" onClick={() => navigate('/online-lobby')}>
          <LogOut size={18} className="mr-1" />
          退出房间
        </ThemedButton>
        <ThemedButton onClick={() => window.location.reload()}>
          <RefreshCw size={18} className="mr-1" />
          刷新房间
        </ThemedButton>
      </footer>

      {/* 弹窗组件 */}
      <AssassinationModal
        isOpen={showAssassination}
        players={players.map((p) => ({ ...p, isEvil: p.seatNumber > 5 }))}
        onClose={() => setShowAssassination(false)}
        onConfirm={(seat) => {
          console.log('刺杀:', seat);
          setShowAssassination(false);
        }}
      />

      <MVPVotingModal
        isOpen={showMVPVoting}
        players={players.map((p) => ({ ...p, voteCount: Math.floor(Math.random() * 5) }))}
        onClose={() => setShowMVPVoting(false)}
        onVote={(seat) => {
          console.log('MVP投票:', seat);
          setShowMVPVoting(false);
        }}
      />

      <LadyOfLakeModal
        isOpen={showLadyOfLake}
        players={players.map((p) => ({ ...p, team: p.seatNumber > 5 ? 'evil' : 'good' }))}
        onClose={() => setShowLadyOfLake(false)}
        onSelect={(seat) => {
          console.log('湖中仙女查看:', seat);
          setShowLadyOfLake(false);
        }}
      />

      <ExcaliburModal
        isOpen={showExcalibur}
        players={players
          .filter((p) => nominatedSeats.includes(p.seatNumber))
          .map((p) => ({ ...p, missionResult: 'success' as const }))}
        onClose={() => setShowExcalibur(false)}
        onReverse={(seat) => {
          console.log('王者之剑反转:', seat);
          setShowExcalibur(false);
        }}
      />

      <GameNotesModal
        isOpen={showNotes}
        initialNotes={gameNotes}
        onClose={() => setShowNotes(false)}
        onSave={(notes) => {
          setGameNotes(notes);
          setShowNotes(false);
        }}
      />

      <IdentityRevealModal
        isOpen={showIdentity}
        currentPlayer={{
          role: '梅林',
          team: 'good',
          visiblePlayers: players
            .filter((p) => p.seatNumber > 5)
            .map((p) => ({ ...p, team: 'evil' as const, isVisible: true })),
        }}
        onClose={() => setShowIdentity(false)}
      />

      <GameHistoryModal
        isOpen={showHistory}
        missions={[
          {
            round: 1,
            leader: 'Drunk',
            leaderSeat: 1,
            team: [1, 2, 3],
            votes: players.map((p) => ({
              playerId: p.id,
              nickname: p.nickname,
              seatNumber: p.seatNumber,
              vote: p.seatNumber <= 6 ? 'approve' : 'reject',
            })),
            result: 'success',
            failCount: 0,
          },
        ]}
        onClose={() => setShowHistory(false)}
      />

      <GameRulesModal
        isOpen={showRules}
        onClose={() => setShowRules(false)}
      />
    </div>
  );
}

// 玩家头像组件
function PlayerAvatar({
  player,
  isLeader,
  isNominated,
  onClick,
  canClick,
}: {
  player: Player;
  isLeader: boolean;
  isNominated: boolean;
  onClick: () => void;
  canClick: boolean;
}) {
  const { theme } = useTheme();

  return (
    <div
      onClick={canClick ? onClick : undefined}
      className={`relative flex flex-col items-center p-2 rounded-lg ${
        canClick ? 'cursor-pointer hover:opacity-80' : ''
      }`}
      style={{
        backgroundColor: player.isCurrentPlayer
          ? `${theme.colors['accent-gold']}15`
          : theme.colors['bg-card'],
        border: `2px solid ${
          isNominated
            ? '#4ade80'
            : isLeader
            ? theme.colors['accent-gold']
            : `${theme.colors['border-secondary']}40`
        }`,
        opacity: player.isOnline ? 1 : 0.5,
      }}
    >
      {/* 头像 */}
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mb-1"
        style={{
          backgroundColor: theme.colors['accent-purple'],
          color: theme.colors['bg-primary'],
        }}
      >
        {player.nickname[0]}
      </div>

      {/* 昵称 */}
      <span
        className="text-xs truncate w-full text-center"
        style={{ color: theme.colors['text-primary'] }}
      >
        {player.nickname}
      </span>

      {/* 座位号 */}
      <span
        className="text-xs"
        style={{ color: theme.colors['text-muted'] }}
      >
        #{player.seatNumber}
      </span>

      {/* 队长标记 */}
      {isLeader && (
        <div
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: theme.colors['accent-gold'],
          }}
        >
          <Crown size={10} style={{ color: theme.colors['bg-primary'] }} />
        </div>
      )}

      {/* 湖中仙女标记 */}
      {player.isLadyOfLake && (
        <div
          className="absolute -top-1 -left-1 w-4 h-4 rounded-full flex items-center justify-center text-xs"
          style={{
            backgroundColor: '#4ade80',
            color: theme.colors['bg-primary'],
          }}
        >
          仙
        </div>
      )}
    </div>
  );
}
